<template>
  <div class="cost-calculator-row">
    <div size="6" class="result-container" :class="variant">
      <div class="cost-calculator">
        <row>
          <column size="4" class="calculator-column">
            <div class="calculator-text">{{$t("ItemLength")}}</div>
            <sb-field>
              <sb-number-input
                v-model="itemLength"
                :min="0"
                :value="itemLength"
                @input="() => handleInputChange()"
                :placeholder="$t('ItemLengthPlaceHolder')"
                numberType="wholeNumber"
              />
            </sb-field>
          </column>
          <column size="4" class="calculator-column">
            <div class="calculator-text">{{$t("ItemWidth")}}</div>
            <sb-field>
              <sb-number-input
                v-model="itemWidth"
                :min="0"
                :value="itemWidth"
                @input="() => handleInputChange()"
                :placeholder="$t('ItemWidthPlaceHolder')"
                numberType="wholeNumber"
              />
            </sb-field>
          </column>
          <column size="4" class="calculator-column">
            <div class="calculator-text">{{$t("ItemHeight")}}</div>
            <sb-field>
              <sb-number-input
                v-model="itemHeight"
                :min="0"
                :value="itemHeight"
                @input="() => handleInputChange()"
                :placeholder="$t('ItemHeightPlaceHolder')"
                numberType="wholeNumber"
              />
            </sb-field>
          </column>
        </row>
        <row>
          <column size="12" class="calculator-column">
            <div class="calculator-text">{{$t("ItemWeight")}}</div>
            <sb-field>
              <sb-number-input
                v-model="itemWeight"
                :min="0"
                :value="itemWeight"
                @input="() => handleInputChange()"
                :placeholder="$t('ItemWeightPlaceHolder')"
                numberType="wholeNumber"
              />
              <div class="weight-unit-selection">
                <div
                  :class="itemWeightUnit ? 'unit-selected' : 'unit-unselected'"
                  @click.prevent="handlePoundsSelected"
                >{{$t("PoundsUnitDescription")}}</div> |
                <div
                  :class="!itemWeightUnit ? 'unit-selected' : 'unit-unselected'"
                  @click.prevent="handleOuncesSelected"
                >{{$t("OuncesUnitDescription")}}</div>
              </div>
            </sb-field>
          </column>
        </row>
        <row>
          <column size="12" class="calculator-column">
            <div class="calculator-text">{{$t("ItemPackaging")}}</div>
            <sb-dropdown
              v-if="packagePreferences && packagePreferences.length"
              v-model="itemPackage"
              @input="() => handleInputChange()"
              >
                <sb-selected-option slot="selection" slot-scope="{ selected }">{{ getSelectedOption(selected) }}</sb-selected-option>
                <sb-options>
                  <sb-option v-for="option of packagePreferences" :key="option.value" :value="option.value">{{option.text}}</sb-option>
                </sb-options>
            </sb-dropdown>
          </column>
        </row>
        <row>
          <column size="12" class="calculator-column-button">
            <button
              :class="!!calculateEnabled ? 'btn2 btn2-calculator-primary' : 'btn2 btn2-calculator-gray'"
              :disabled="!calculateEnabled"
              @click.prevent="handleCalculate" href="#"
            >{{$t("ButtonCalculate")}}</button>
          </column>
        </row>
        <row v-if='!!handleSkip'>
          <column size="12" class="calculator-column">
            <div class="calculator-subtext">{{$t("DontWorryText")}}
              <a @click.prevent="handleSkip" href="#" class="cost-calculator-skip">{{$t("SkipThisStepText")}}</a> {{$t("ReviewPricingText")}}
            </div>
          </column>
        </row>
      </div>
    </div>
    <div size="6" class="result-container" :class="variant">
      <CostCalculatorResultCart
        v-bind:resultValue="this.estimatedPrice"
        v-bind:isLoading="this.isLoading"
      >
      </CostCalculatorResultCart>
    </div>
  </div>
</template>

<script>
import CostCalculatorResultCart from './CostCalculatorResultCart.vue'
import { OnboardingSteps } from '@enums/modules/onboarding/onboardingSteps'
import { OnboardingStepEvents } from '@enums/modules/onboarding/onboardingStepEvents'
import { packageTypeEnumMap } from '@/mappers/products/product'
import ShipbobAppSettings from '../../../appSettings/appSetting'

const defaultOrderTypeId = 1
const packageTypeIdsToIgnore = ['0', '6']

export default {
  components: {
    CostCalculatorResultCart
  },
  props: {
    itemLengthProp: {
      type: Number,
      default: undefined
    },
    itemWidthProp: {
      type: Number,
      default: undefined
    },
    itemHeightProp: {
      type: Number,
      default: undefined
    },
    itemWeightProp: {
      type: Number,
      default: undefined
    },
    itemWeightUnitProp: {
      type: Number,
      default: 0
    },
    itemPackageProp: {
      type: Number,
      default: 1
    },
    priceResultProp: {
      type: String,
      default: undefined
    },
    sendOnboardingEvent: {
      type: Boolean,
      default: false
    },
    variant: {
      type: String,
      default: ''
    },
    handleSkip: {
      type: Function,
      default: undefined
    }
  },
  data () {
    return {
      itemLength: undefined,
      itemWidth: undefined,
      itemHeight: undefined,
      itemWeight: undefined,
      itemWeightUnit: 0,
      itemPackage: 1,
      priceResult: undefined,
      isLoading: false,
      calculated: false,
      changedInput: false
    }
  },
  mounted () {
    if (this.itemLengthProp) {
      this.itemLength = this.itemLengthProp
    }
    if (this.itemWidthProp) {
      this.itemWidth = this.itemWidthProp
    }
    if (this.itemHeightProp) {
      this.itemHeight = this.itemHeightProp
    }
    if (this.itemWeightProp) {
      this.itemWeight = this.itemWeightProp
    }
    if (this.itemWeightUnitProp) {
      this.itemWeightUnit = this.itemWeightUnitProp
    }
    if (this.itemPackageProp) {
      this.itemPackage = this.itemPackageProp
    }
    if (this.priceResultProp) {
      this.priceResult = this.priceResultProp
      this.calculated = true
    }
    this.changedInput = false
  },
  created () {
  },
  computed: {
    user () {
      return this.$shipBobStore.getters['user/getUser']
    },
    calculateEnabled () {
      return (this.itemLength && this.itemWidth && this.itemHeight && this.itemWeight &&
        this.itemLength > 0 && this.itemWidth > 0 && this.itemHeight > 0 && this.itemWeight > 0 &&
        this.changedInput)
    },
    packagePreferences () {
      const packagePreferencesArr = []
      for (const key in packageTypeEnumMap) {
        if (!packageTypeIdsToIgnore.includes(key)) {
          packagePreferencesArr.push({
            value: +key,
            text: packageTypeEnumMap[key]
          })
        }
      }

      return packagePreferencesArr
    },
    pricingPlan () {
      return this.$shipBobStore.getters['pricing/pricingPlan']
    },
    billableWeightEstimate () {
      return this.$shipBobStore.getters['pricing/billableWeightEstimate']
    },
    pricingPlans () {
      return this.pricingPlan && this.pricingPlan.pricingRates
    },
    estimatedWeight () {
      return this.billableWeightEstimate.billableWeight
    },
    dimensions () {
      return {
        Length: this.itemLength,
        Width: this.itemWidth,
        Height: this.itemHeight,
        Weight: this.itemWeight,
        WeightUnit: this.itemWeightUnit,
        Package: this.itemPackage
      }
    },
    estimatedPrice () {
      if (this.calculated &&
        this.estimatedWeight &&
        this.pricingPlans.length > 0) {
        var calculatedPrice = this.calculatePrice()
        if (this.sendOnboardingEvent && calculatedPrice && calculatedPrice.length === 2) {
          try {
            this.$shipBobStore.dispatch('onboarding/setOnboardingStepState', {
              id: OnboardingSteps.CostCalculator,
              stepState: {
                'Dimensions': this.dimensions,
                'PriceResult': calculatedPrice.toString()
              }
            })
            this.$shipBobStore.dispatch('onboarding/setOnboardingStepEvent', {
              stepId: OnboardingSteps.CostCalculator,
              eventId: OnboardingStepEvents.CostCalculatorResult,
              eventValue: calculatedPrice.toString()
            })
          } catch (error) {}
        }
        return calculatedPrice.toString()
      }
      return this.priceResult
    }
  },
  methods: {
    calculatePrice () {
      var exactPricePlan = this.pricingPlans.find(x => x.weightRange[0] <= this.estimatedWeight && x.weightRange[1] >= this.estimatedWeight)
      var higherPricePlan = this.pricingPlans.find(x => x.weightRange[1] > this.estimatedWeight)

      // If two plans are found compare them and if there is an overlap take the higher one
      if ((exactPricePlan && exactPricePlan.zonePrices) &&
        (higherPricePlan && higherPricePlan.zonePrices) &&
        exactPricePlan.weightRange[1] === higherPricePlan.weightRange[0]) {
        return higherPricePlan.zonePrices.map(zone => zone.price && Number.isFinite(zone.price) ? zone.price.toFixed(1) : zone.price)
      }

      // If the exact slab was found for the weight
      if (exactPricePlan && exactPricePlan.zonePrices) {
        return exactPricePlan.zonePrices.map(zone => zone.price && Number.isFinite(zone.price) ? zone.price.toFixed(1) : zone.price)
      }

      // If not, take the next higher slab
      if (higherPricePlan && higherPricePlan.zonePrices) {
        return higherPricePlan.zonePrices.map(zone => zone.price && Number.isFinite(zone.price) ? zone.price.toFixed(1) : zone.price)
      }
      return undefined
    },
    handleInputChange () {
      this.changedInput = true
    },
    handlePoundsSelected () {
      this.changedInput = true
      this.itemWeightUnit = 1
    },
    handleOuncesSelected () {
      this.changedInput = true
      this.itemWeightUnit = 0
    },
    async handleCalculate () {
      this.changedInput = false
      this.isLoading = true
      this.calculated = true
      try {
        var data = {
          orderTypeId: defaultOrderTypeId,
          userId: this.user.impersonatedUserId || this.user.id,
          items: [{
            itemLength: this.itemLength,
            itemWidth: this.itemWidth,
            itemHeight: this.itemHeight,
            itemWeight: this.itemWeightUnit ? this.itemWeight * 16 : this.itemWeight,
            itemQuantity: 1,
            itemPreference: this.itemPackage
          }]
        }

        if (this.sendOnboardingEvent) {
          this.$shipBobStore.dispatch('onboarding/setOnboardingStepState', {
            id: OnboardingSteps.CostCalculator,
            stepState: {
              'Dimensions': this.dimensions
            }
          })
          this.$shipBobStore.dispatch('onboarding/setOnboardingStepEvent', {
            stepId: OnboardingSteps.CostCalculator,
            eventId: OnboardingStepEvents.CostCalculatorDimensions,
            eventValue: JSON.stringify(this.dimensions)
          })
        }

        var promises = [
          this.$shipBobStore.dispatch('pricing/calculatePackageSize', data)
        ]

        if (this.pricingPlans.length === 0) {
          var shipOptionId = ShipbobAppSettings.OnboardingWizard.ShipOptionId
          var zoneIds = [
            ShipbobAppSettings.OnboardingWizard.MinPricingZoneId,
            ShipbobAppSettings.OnboardingWizard.MaxPricingZoneId
          ]
          promises.push(this.$shipBobStore.dispatch('pricing/getExplicitPricingPlan', { shipOptionId, zoneIds }))
        }
        await Promise.all(promises)
        // Scroll to the result card
        var cardElement = document.getElementById('costCalculatorResultCard')
        window.scrollTo({ top: cardElement.clientHeight, behavior: 'smooth' })
      } catch (error) { }
      this.isLoading = false
    },
    getSelectedOption (optionValue) {
      return this.packagePreferences.find(pp => pp.value === optionValue).text
    }
  },
  watch: {
  },
  i18n: {
    messages: {
      'en-US': {
        ButtonCalculate: 'Calculate',
        ItemLength: 'Length (in)',
        ItemLengthPlaceHolder: 'Length',
        ItemWidth: 'Width (in)',
        ItemWidthPlaceHolder: 'Width',
        ItemHeight: 'Height (in)',
        ItemHeightPlaceHolder: 'Height',
        ItemWeight: 'Item Weight',
        ItemWeightPlaceHolder: 'Item Weight',
        ItemPackaging: 'Packaging',
        PoundsUnitDescription: 'lb',
        OuncesUnitDescription: 'oz',
        DontWorryText: 'Don’t worry, you can always',
        SkipThisStepText: 'skip this step',
        ReviewPricingText: 'and review pricing later in our dashboard.'
      }
    }
  }
}
</script>

<style scoped lang="scss">
@import '../onboarding.scss';
@import './CostCalculator.scss';
</style>